import { type Role, type RoleDefaultScope } from '@/services/models'
import { type FilterState, type IngestMetaState, useDashboardFiltersStore } from '@/stores/dashboard-filters'
import { BankApi } from '@/services/api/bank'
import { RoleApi } from '@/services/api/role'


/** Top-bar branch / relationship-manager scope as the store keeps it. */
export interface DashboardScope {
  branches: string[]
  relationshipManagers: string[]
}

/**
 * The scope in API wire form: only the non-empty lists, under the names every
 * scoped endpoint takes (`branches`, `relationship_managers`). Merge it into a
 * request payload the same way `getDateRangeStrings()` is used.
 */
export interface ScopeFilters {
  branches?: string[]
  relationship_managers?: string[]
}

function normalizeScopeList(values: string[]): string[] {
  const unique = new Set<string>()
  for (const raw of values) {
    const value = (raw ?? '').trim()
    if (value) unique.add(value)
  }
  // numeric-aware so "4 - Port Jamesbury" sorts before "10 - Markbury"
  return Array.from(unique).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
}

function sameList(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index])
}

/** The sign-in scope saved on a role, normalised (no role / no default = every account). */
function scopeFromRole(role: Role | null | undefined): DashboardScope {
  return {
    branches: normalizeScopeList(role?.default_scope?.branches ?? []),
    relationshipManagers: normalizeScopeList(role?.default_scope?.relationship_managers ?? []),
  }
}


function getStore() {
  const store = useDashboardFiltersStore()

  // MIGRATION: Ensure every role has a `permissions` property
  const hasMissingPermission = store.roles.some((r: Record<string, unknown>) => !('permissions' in r))
  if (hasMissingPermission) {
    store.reset()
  }

  return store
}

export class DashboardFilters {
  /**
   * Format a Date to YYYY-MM-DD using local date parts (timezone-safe).
   */
  static formatDate(date: Date): string {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, '0')
    const d = String(date.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
  }

  /**
   * Parse a YYYY-MM-DD (or ISO datetime) string as local midnight (timezone-safe).
   */
  private static parseLocalDate(dateStr: string): Date {
    const [y, m, d] = dateStr.substring(0, 10).split('-').map(Number)
    return new Date(y, m - 1, d)
  }

  static saveAllRoles(roles: Role[]) {
    const store = getStore()
    store.setRoles(roles)
  }

  static setRole(id: string) {
    const store = getStore()

    // Validate if the role exists
    const role = store.roles.find((r) => r.id === id)
    if (!role) {
      throw new Error(`Role with id ${id} not found`)
    }

    store.setRole(role)
  }

  /**
   * Switch to a new role/bank: set the role, fetch IngestMeta for the new bank,
   * and reset the date range to last 365 days.
   *
   * The refresh is signalled exactly once, at the end, via bumpRevision(). This
   * guarantees the dashboard reloads for the new bank even when its default
   * date range is identical to the previous bank's (or when the IngestMeta
   * fetch fails), which previously left the screen showing the old bank's data.
   */
  static async switchRole(id: string) {
    this.setRole(id)
    // Branch / RM names are tenant-specific: a scope never carries over to
    // another bank — the new bank starts from that role's sign-in scope. No
    // refresh signal here: the single bump below covers it.
    getStore().setScope(scopeFromRole(getStore().currentRole))
    await this.fetchIngestMetaAndSetDefaults()
    getStore().bumpRevision()
  }

  static restorePreviousRoleOrSetDefault(defaultID: string) {
    const store = getStore()

    const hasPreviousRole = store.currentRole
    const isPreviousRoleValid = hasPreviousRole && store.roles.some((r) => r.id === store.currentRole?.id)

    // If currentRole is not set, or is invalid, try to find the default role
    if (!hasPreviousRole || !isPreviousRoleValid) {
      const defaultRole = store.roles.find((r) => r.id === defaultID)
      if (defaultRole) {
        store.setRole(defaultRole)
      } else {
        throw new Error(`Default role with id ${defaultID} not found`)
      }
    }
  }

  // New method to ensure currentRole is always updated after login
  static async initializeRolesAfterLogin(roles: Role[]) {
    // First save all roles
    this.saveAllRoles(roles)

    // Then set default role if needed
    if (roles && roles.length > 0) {
      const store = getStore()

      // If there's no current role or it's invalid, set the first role as current
      if (!store.currentRole || !roles.some(r => r.id === store.currentRole?.id)) {
        this.setRole(roles[0].id)
      } else {
        // If there is a current role, but its data might be stale, refresh it
        const updatedCurrentRole = roles.find(r => r.id === store.currentRole?.id)
        if (updatedCurrentRole) {
          store.setRole(updatedCurrentRole)
        }
      }

      // every sign-in starts from the role's saved branch / RM scope (empty = every account)
      store.setScope(scopeFromRole(store.currentRole))
    }

    // Fetch IngestMeta and set default date range before dashboard mounts
    await this.fetchIngestMetaAndSetDefaults()
  }

  static getAllRoles(): Role[] {
    const store = getStore()
    return store.roles
  }

  static getCurrentRole(): Role {
    const store = getStore()
    // Get the currentRole or the first role in the list
    return store.currentRole || store.roles[0]
  }

  /**
   * Write the date range to the store WITHOUT signalling a refresh. Used
   * internally while resolving a bank's defaults so the refresh can be fired
   * once, coherently, after all filters are settled.
   */
  private static applyDateRange(start: Date, end: Date) {
    const store = getStore()
    store.setDateRange({ start: this.formatDate(start), end: this.formatDate(end) })
  }

  /**
   * Public date-range change (e.g. the date picker). Applies the range and
   * signals subscribers to refresh.
   */
  static setDateRange(start: Date, end: Date) {
    this.applyDateRange(start, end)
    getStore().bumpRevision()
  }

  static getDateRange(): { start: Date; end: Date } {
    const store = getStore()
    if (!store.startDate || !store.endDate) {
      // If IngestMeta is available, default to last 365 days from last transaction
      if (store.ingestMeta?.lastTransactionDate) {
        const end = this.parseLocalDate(store.ingestMeta.lastTransactionDate)
        const start = new Date(end)
        start.setDate(start.getDate() - 364)
        return { start, end }
      }
      // Last resort fallback (should not happen after login)
      const now = new Date()
      const yearAgo = new Date()
      yearAgo.setDate(yearAgo.getDate() - 364)
      return { start: yearAgo, end: now }
    }

    return { start: this.parseLocalDate(store.startDate), end: this.parseLocalDate(store.endDate) }
  }

  /**
   * Get date range as YYYY-MM-DD strings, ready for API payloads.
   * Centralized to avoid timezone bugs from toISOString().split('T')[0].
   */
  static getDateRangeStrings(): { date_start: string; date_end: string } {
    const { start, end } = this.getDateRange()
    return { date_start: this.formatDate(start), date_end: this.formatDate(end) }
  }

  // ── branch / relationship-manager scope ──────────────────────────────────

  /**
   * Apply the top-bar scope and signal a refresh, exactly like a date-range
   * change. Returns false (and signals nothing) when the normalised scope is
   * the one already applied.
   */
  static setScope(scope: DashboardScope): boolean {
    const store = getStore()
    const next = {
      branches: normalizeScopeList(scope.branches),
      relationshipManagers: normalizeScopeList(scope.relationshipManagers),
    }
    if (sameList(next.branches, store.branches) && sameList(next.relationshipManagers, store.relationshipManagers)) {
      return false
    }
    store.setScope(next)
    store.bumpRevision()
    return true
  }

  static clearScope(): boolean {
    return this.setScope({ branches: [], relationshipManagers: [] })
  }

  static getScope(): DashboardScope {
    const store = getStore()
    return { branches: [...store.branches], relationshipManagers: [...store.relationshipManagers] }
  }

  static hasScope(): boolean {
    const store = getStore()
    return store.branches.length > 0 || store.relationshipManagers.length > 0
  }

  /** Scope for API payloads: only the non-empty lists, under the API's names. */
  static getScopeFilters(): ScopeFilters {
    const store = getStore()
    const filters: ScopeFilters = {}
    if (store.branches.length > 0) filters.branches = [...store.branches]
    if (store.relationshipManagers.length > 0) filters.relationship_managers = [...store.relationshipManagers]
    return filters
  }

  // ── sign-in default (saved on the role, applied at every login / bank switch) ──

  /** The sign-in scope saved on the current role (empty lists = none). */
  static getDefaultScope(): DashboardScope {
    return scopeFromRole(getStore().currentRole)
  }

  static hasDefaultScope(): boolean {
    const saved = this.getDefaultScope()
    return saved.branches.length > 0 || saved.relationshipManagers.length > 0
  }

  /** True when `scope` is exactly the current role's sign-in scope. */
  static isDefaultScope(scope: DashboardScope): boolean {
    const saved = this.getDefaultScope()
    return (
      sameList(normalizeScopeList(scope.branches), saved.branches) &&
      sameList(normalizeScopeList(scope.relationshipManagers), saved.relationshipManagers)
    )
  }

  /** Save `scope` as the current role's sign-in default (does not change the scope in use). */
  static async saveScopeAsDefault(scope: DashboardScope): Promise<void> {
    const saved = await RoleApi.setDefaultScope({
      branches: normalizeScopeList(scope.branches),
      relationship_managers: normalizeScopeList(scope.relationshipManagers),
    })
    this.applySavedDefault(saved)
  }

  static async clearDefaultScope(): Promise<void> {
    this.applySavedDefault(await RoleApi.clearDefaultScope())
  }

  /** Keep the stored role (current and in the list) in step with what the server saved. No refresh signal. */
  private static applySavedDefault(saved: RoleDefaultScope) {
    const store = getStore()
    const current = store.currentRole
    if (!current) return
    const updated: Role = { ...current, default_scope: saved }
    store.setRole(updated)
    store.setRoles(store.roles.map((role) => (role.id === updated.id ? updated : role)))
  }

  /**
   * Drop scope values the bank no longer offers (a persisted scope can name a
   * branch or RM that a later data refresh renamed). Signals a refresh only
   * when something was actually dropped.
   */
  static pruneScope(available: DashboardScope): boolean {
    const store = getStore()
    const branches = new Set(available.branches)
    const managers = new Set(available.relationshipManagers)
    const next = {
      branches: store.branches.filter((value) => branches.has(value)),
      relationshipManagers: store.relationshipManagers.filter((value) => managers.has(value)),
    }
    if (next.branches.length === store.branches.length && next.relationshipManagers.length === store.relationshipManagers.length) {
      return false
    }
    store.setScope(next)
    store.bumpRevision()
    return true
  }

  static reset() {
    const store = getStore()
    store.reset()
  }

  static setIngestMeta(meta: IngestMetaState | null) {
    const store = getStore()
    store.setIngestMeta(meta)
  }

  static getIngestMeta(): IngestMetaState | null {
    const store = getStore()
    return store.ingestMeta
  }

  /**
   * Fetch bank summary to get IngestMeta, then set the default 365-day date range.
   * Called during login before the dashboard mounts.
   */
  static async fetchIngestMetaAndSetDefaults() {
    try {
      const bankData = await BankApi.summary()
      if (bankData?.ingest_meta) {
        this.setIngestMeta({
          firstTransactionDate: bankData.ingest_meta.first_transaction_date,
          lastTransactionDate: bankData.ingest_meta.last_transaction_date,
        })

        // Set default date range: last 365 days from last transaction date
        // "Last 365 days" includes the end date, so subtract 364.
        // Use applyDateRange (no refresh signal) — callers (switchRole /
        // login) decide when to signal the single refresh.
        const lastDate = this.parseLocalDate(bankData.ingest_meta.last_transaction_date)
        const startDate = new Date(lastDate)
        startDate.setDate(startDate.getDate() - 364)
        this.applyDateRange(startDate, lastDate)
      }
    } catch (e) {
      console.error('Failed to fetch IngestMeta during login:', e)
    }
  }

  static subscribe(callback: (state: FilterState) => void) {
    const store = getStore()
    let prevRevision = store.revision

    store.$subscribe((_mutation, state) => {
      // Refresh whenever the revision counter is bumped. A bump happens exactly
      // once per user-facing filter change — a bank switch (switchRole) or a
      // date-range change (setDateRange) — so this fires once per change,
      // including bank switches whose resulting date range is unchanged.
      // Background role/permission syncs (setRole) do NOT bump, so they never
      // trigger a dashboard reload.
      if (state.revision !== prevRevision) {
        prevRevision = state.revision
        callback(state)
      }
    })
  }
}
